<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'

interface Props {
  show: boolean
  title?: string
  size?: 'sm' | 'md' | 'lg'
  /**
   * `false` ⇒ modal bloqueante: no se cierra con Escape, ni con clic fuera, ni
   * muestra la X. Lo usa el consentimiento de los términos (ADR-SEC-013 local).
   */
  dismissable?: boolean
  /** id del elemento que titula el diálogo cuando el título va en el cuerpo. */
  labelledBy?: string
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  size: 'md',
  dismissable: true,
  labelledBy: undefined,
})

const emit = defineEmits<{
  close: []
}>()

const panelRef = ref<HTMLElement | null>(null)

const sizeClasses: Record<string, string> = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' && props.show && props.dismissable) {
    emit('close')
  }
}

function onBackdropClick(e: MouseEvent) {
  if (!props.dismissable) return
  if (panelRef.value && !panelRef.value.contains(e.target as Node)) {
    emit('close')
  }
}

onMounted(() => {
  document.addEventListener('keydown', onKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', onKeydown)
})
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-300"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-300"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="show"
        class="fixed inset-0 z-50 flex items-center justify-center p-4"
        @mousedown="onBackdropClick"
      >
        <!-- Backdrop -->
        <div class="absolute inset-0 bg-black/50" />

        <!-- Panel -->
        <div
          ref="panelRef"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="labelledBy"
          class="relative w-full bg-white rounded-card shadow-xl"
          :class="sizeClasses[size]"
        >
          <!-- Header -->
          <div
            v-if="title || $slots.header"
            class="flex items-center justify-between px-6 py-4 border-b border-border"
          >
            <slot name="header">
              <h2 class="text-lg font-semibold text-slate-900">
                {{ title }}
              </h2>
            </slot>

            <button
              v-if="dismissable"
              class="p-1 text-slate-400 hover:text-slate-600 transition-colors duration-200 rounded-lg hover:bg-surface-alt"
              aria-label="Cerrar"
              @click="emit('close')"
            >
              <svg
                class="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <!-- Body -->
          <div class="px-6 py-4">
            <slot />
          </div>

          <!-- Footer -->
          <div
            v-if="$slots.footer"
            class="px-6 py-4 border-t border-border"
          >
            <slot name="footer" />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
